/**
 * Shared compliance modal
 * -----------------------
 * The grade popup that Confluent users already get when they click the
 * Clinicient sign-off pen. Free Compliance needs the same popup from
 * a completely different surface -- the floating score bubble -- so the markup
 * builders live here and both callers render them.
 *
 * Everything here is plain DOM/HTML string work on purpose: it renders into the
 * EMR page from a content script, where there is no React root to hang off.
 */

export type ComplianceReason = {
  status?: string;
  requirement_text?: string;
  title?: string;
  suggestion?: string;
};

export function compGradeColor(grade: string): string {
  if (grade === 'A') return '#6bd888';
  if (grade === 'B') return 'orange';
  if (grade === 'C') return '#FACC15';
  return 'red';
}

function escapeHtml(value: unknown): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function byStatus(reasons: ComplianceReason[], status: string): ComplianceReason[] {
  return (reasons || []).filter(
    (r) => (r?.status || '').toLowerCase() === status
  );
}

function renderItems(
  items: ComplianceReason[],
  dotColor: string,
  icon: string
): string {
  return items
    .map(
      (r) =>
        `<div style="background:white;border:1px solid #f3f4f6;border-radius:8px;` +
        `padding:12px 14px;margin-bottom:8px;display:flex;gap:10px;align-items:flex-start;">` +
        `<div style="width:18px;height:18px;border-radius:50%;background:${dotColor};` +
        `flex-shrink:0;margin-top:2px;display:flex;align-items:center;justify-content:center;` +
        `font-size:11px;font-weight:700;color:white;">${icon}</div>` +
        `<div><p style="margin:0;font-size:13px;font-weight:600;color:#111827;">` +
        `${escapeHtml(r.requirement_text || r.title || '')}</p>` +
        (r.suggestion
          ? `<p style="margin:4px 0 0 0;font-size:12px;color:#6b7280;">${escapeHtml(
              r.suggestion
            )}</p>`
          : '') +
        `</div></div>`
    )
    .join('');
}

/** The "Not Met" / "Partially Met" lists, empty string when the note is clean. */
export function buildComplianceItemsHTML(reasons: ComplianceReason[]): string {
  const notMet = byStatus(reasons, 'not met');
  const partiallyMet = byStatus(reasons, 'partially met');

  const notMetSection =
    notMet.length > 0
      ? `<p style="font-size:11px;font-weight:700;letter-spacing:0.06em;color:red;` +
        `text-transform:uppercase;margin:0 0 8px 0;">Not Met (${notMet.length})</p>` +
        renderItems(notMet, 'red', '✕')
      : '';

  const partiallyMetSection =
    partiallyMet.length > 0
      ? `<p style="font-size:11px;font-weight:700;letter-spacing:0.06em;color:orange;` +
        `text-transform:uppercase;margin:${notMet.length > 0 ? '12px' : '0'} 0 8px 0;">` +
        `Partially Met (${partiallyMet.length})</p>` +
        renderItems(partiallyMet, 'orange', '~')
      : '';

  return notMetSection + partiallyMetSection;
}

export function complianceGapCount(reasons: ComplianceReason[]): number {
  return byStatus(reasons, 'not met').length + byStatus(reasons, 'partially met').length;
}

export const COMPLIANCE_FOOTER_HTML =
  `<p style="font-size:11px;color:#9ca3af;margin:0;text-align:center;">` +
  `Powered by Comprehend PT</p>`;

/**
 * Body of the grade popup: header band with the letter grade, the gap lists,
 * the guidance line and whatever action row the caller wants under it.
 *
 * Grade A gets the same header treatment with a congratulatory body, so a
 * caller that has nowhere to put a separate tooltip (the score bubble) can show
 * one popup for every grade.
 */
export function buildComplianceModalHTML(
  grade: string,
  reasons: ComplianceReason[],
  opts: { guidance?: string; actionsHtml?: string; closeButtonId?: string } = {}
): string {
  const color = compGradeColor(grade);
  const closeId = opts.closeButtonId || 'comp-close-btn';
  const totalCount = complianceGapCount(reasons);

  const subtitle =
    grade === 'A'
      ? 'Meets documentation criteria'
      : grade === 'B'
        ? 'Partially met documentation criteria'
        : grade === 'C'
          ? 'Multiple criteria not fully met'
          : 'Note does not meet documentation standards';

  const title =
    grade === 'A'
      ? 'Grade A — compliant'
      : grade === 'F'
        ? 'Grade F — critical gaps'
        : `Grade ${grade} — ${totalCount} item${totalCount !== 1 ? 's' : ''} to address`;

  const headerBg =
    grade === 'A'
      ? 'rgba(107,216,136,0.12)'
      : grade === 'B'
        ? 'rgba(224,168,0,0.10)'
        : grade === 'C'
          ? 'rgba(232,114,42,0.10)'
          : 'rgba(208,52,44,0.10)';

  const itemsHtml = buildComplianceItemsHTML(reasons);

  const header =
    `<div style="background:${headerBg};border-radius:12px 12px 0 0;padding:20px 20px 16px;` +
    `margin:-20px -20px 16px;position:relative;">` +
    `<button id="${closeId}" style="position:absolute;top:14px;right:14px;` +
    `background:none;border:none;cursor:pointer;font-size:18px;color:#6b7280;` +
    `line-height:1;padding:0;">×</button>` +
    `<p style="margin:0 0 8px 0;font-size:11px;font-weight:700;letter-spacing:0.08em;` +
    `color:${color};text-transform:uppercase;">Comprehend Compliance</p>` +
    `<div style="display:flex;align-items:center;gap:12px;">` +
    `<div style="width:44px;height:44px;border-radius:50%;background:${color};display:flex;` +
    `align-items:center;justify-content:center;font-size:22px;font-weight:700;color:white;` +
    `font-family:system-ui;flex-shrink:0;">${escapeHtml(grade)}</div>` +
    `<div><p style="font-size:18px;font-weight:700;margin:0;color:${color};">${title}</p>` +
    `<p style="font-size:13px;color:#6b7280;margin:2px 0 0 0;">${subtitle}</p>` +
    `</div></div></div>`;

  const guidance =
    opts.guidance !== undefined
      ? opts.guidance
      : grade === 'A'
        ? 'This note meets CMS documentation guidelines.'
        : 'Record more information or edit the note in your EMR to raise the grade, ' +
          'then score again.';

  return (
    header +
    (itemsHtml
      ? `<div style="max-height:260px;overflow-y:auto;margin-bottom:14px;">${itemsHtml}</div>`
      : '') +
    (guidance
      ? `<p style="font-size:12px;color:#6b7280;margin:0 0 16px 0;line-height:1.5;">${guidance}</p>`
      : '') +
    (opts.actionsHtml || '') +
    COMPLIANCE_FOOTER_HTML
  );
}

/** Spinner card shown while the score request is in flight. */
export function buildComplianceLoadingHTML(
  headline = 'Checking compliance...',
  detail = 'Grading the note against CMS documentation criteria',
  closeButtonId = 'comp-loading-close-btn'
): string {
  return (
    `<div style="position:relative;display:flex;flex-direction:column;align-items:center;` +
    `justify-content:center;padding:40px 20px;gap:20px;">` +
    `<button id="${closeButtonId}" style="position:absolute;top:-4px;right:-4px;` +
    `background:none;border:none;cursor:pointer;font-size:18px;color:#9ca3af;` +
    `line-height:1;padding:4px;">×</button>` +
    `<div style="width:56px;height:56px;border-radius:50%;` +
    `border:4px solid #e5e7eb;border-top-color:#6bd888;` +
    `animation:comp-spin 0.8s linear infinite;"></div>` +
    `<div style="text-align:center;">` +
    `<p style="font-size:11px;font-weight:700;letter-spacing:0.1em;` +
    `color:#6bd888;text-transform:uppercase;margin:0 0 8px 0;">Comprehend</p>` +
    `<p style="font-size:16px;font-weight:700;color:#111827;margin:0 0 6px 0;">` +
    `${escapeHtml(headline)}</p>` +
    `<p style="font-size:13px;color:#6b7280;margin:0;">${escapeHtml(detail)}</p>` +
    `</div></div>`
  );
}

export function ensureComplianceSpinnerStyle(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById('comp-spin-style')) return;
  const style = document.createElement('style');
  style.id = 'comp-spin-style';
  style.textContent = '@keyframes comp-spin{to{transform:rotate(360deg)}}';
  document.head.appendChild(style);
}

/* -------------------------------------------------------------------------- */
/* Standalone modal (score bubble)                                            */
/* -------------------------------------------------------------------------- */

const STANDALONE_BACKDROP_ID = 'comp-score-backdrop';

function removeStandalone(): void {
  const existing = document.getElementById(STANDALONE_BACKDROP_ID);
  if (existing) existing.remove();
}

export function hideComplianceModal(): void {
  removeStandalone();
}

/**
 * Open the grade popup on its own backdrop -- no sign-off pen, no EMR button to
 * anchor to. Used by the free-tier score bubble.
 */
export function showComplianceModal(options: {
  grade?: string | null;
  reasons?: ComplianceReason[];
  loading?: boolean;
  loadingHeadline?: string;
  errorMessage?: string;
  onClose?: () => void;
  /** Score again in place. Adds a Re-check button when supplied. */
  onRecheck?: () => void;
}): void {
  if (typeof document === 'undefined' || !document.body) return;
  removeStandalone();
  ensureComplianceSpinnerStyle();

  const backdrop = document.createElement('div');
  backdrop.id = STANDALONE_BACKDROP_ID;
  backdrop.style.cssText =
    'position:fixed;inset:0;z-index:2147483646;background:rgba(0,0,0,0.45);' +
    'display:flex;align-items:center;justify-content:center;';

  const popup = document.createElement('div');
  popup.style.cssText =
    'background:white;border-radius:12px;box-shadow:0 8px 32px rgba(0,0,0,.25);' +
    'padding:20px;width:500px;max-width:90vw;font-family:system-ui,-apple-system,sans-serif;' +
    'max-height:85vh;overflow-y:auto;';

  const close = (): void => {
    removeStandalone();
    if (options.onClose) options.onClose();
  };

  if (options.loading) {
    popup.innerHTML = buildComplianceLoadingHTML(
      options.loadingHeadline || 'Checking compliance...'
    );
  } else if (options.errorMessage) {
    popup.innerHTML =
      `<div style="position:relative;padding:8px 0;">` +
      `<button id="comp-close-btn" style="position:absolute;top:-8px;right:-8px;` +
      `background:none;border:none;cursor:pointer;font-size:18px;color:#6b7280;` +
      `line-height:1;padding:4px;">×</button>` +
      `<p style="margin:0 0 8px 0;font-size:11px;font-weight:700;letter-spacing:0.08em;` +
      `color:#6b7280;text-transform:uppercase;">Comprehend Compliance</p>` +
      `<p style="font-size:15px;font-weight:600;color:#111827;margin:0 0 16px 0;">` +
      `${escapeHtml(options.errorMessage)}</p></div>` +
      COMPLIANCE_FOOTER_HTML;
  } else {
    const color = compGradeColor(options.grade || '--');
    // With a re-check available the two actions sit side by side, so the
    // clinician can edit the note and re-grade without hunting for the rail.
    const recheckBtn = options.onRecheck
      ? `<button id="comp-score-recheck-btn" style="flex:1;padding:11px;border-radius:8px;` +
        `border:1.5px solid #d1d5db;background:white;font-size:13px;font-weight:600;` +
        `color:#374151;cursor:pointer;">Re-check</button>`
      : '';
    popup.innerHTML = buildComplianceModalHTML(
      options.grade || '--',
      options.reasons || [],
      {
        actionsHtml:
          `<div style="display:flex;gap:8px;margin-bottom:16px;">` +
          `<button id="comp-score-done-btn" style="flex:1;padding:11px;border-radius:8px;` +
          `border:none;background:${color};font-size:13px;` +
          `font-weight:600;color:white;cursor:pointer;">Back to note</button>` +
          recheckBtn +
          `</div>`
      }
    );
  }

  popup.addEventListener('click', (e: MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest('#comp-score-recheck-btn')) {
      close();
      if (options.onRecheck) options.onRecheck();
      return;
    }
    if (
      target.closest('#comp-close-btn') ||
      target.closest('#comp-loading-close-btn') ||
      target.closest('#comp-score-done-btn')
    ) {
      close();
    }
  });

  backdrop.addEventListener('click', (e: MouseEvent) => {
    if (e.target === backdrop) close();
  });

  backdrop.appendChild(popup);
  document.body.appendChild(backdrop);
}
